"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, ArrowRight, Building2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function HeadquartersInformation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const contactDetails = [{
    icon: MapPin,
    title: "Địa chỉ trụ sở",
    content: "Phòng 905, Tầng 9, Nhà Thí nghiệm, Đại học Xây dựng, 55 Giải Phóng, Đồng Tâm, Hai Bà Trưng, Hà Nội",
    actionLabel: "Xem bản đồ",
    href: "https://www.google.com/maps/place/C%C3%94NG+TY+CP+%C4%90%E1%BA%A6U+T%C6%AF+V%C3%80+PH%C3%81T+TRI%E1%BB%82N+C%C3%94NG+NGH%E1%BB%86+%C4%90%E1%BA%A0I+H%E1%BB%8CC+X%C3%82Y+D%E1%BB%B0NG+-+NUCETECH/@21.0032167,105.8443898,16z/data=!3m1!4b1!4m6!3m5!1s0x3135ade290963b97:0xe952ce3fd63524a9!8m2!3d21.0032167!4d105.8443898!16s%2Fg%2F11v_5ct2xp?entry=ttu&g_ep=EgoyMDI2MDYxMC4wIKXMDSoASAFQAw%3D%3D",
    isExternal: true
  }, {
    icon: Phone,
    title: "Điện thoại văn phòng",
    content: "(+84-24) 3628 5491",
    actionLabel: "Gọi ngay",
    href: "tel:+84243628491",
    isExternal: true
  }, {
    icon: Mail,
    title: "Email hỗ trợ kỹ thuật",
    content: "info@nucetech.vn",
    actionLabel: "Gửi email",
    href: "mailto:info@nucetech.vn",
    isExternal: true
  }, {
    icon: Clock,
    title: "Giờ làm việc",
    content: "Thứ 2 - Thứ 6: 8:30 - 17:30",
    actionLabel: null,
    href: null,
    isExternal: false
  }];
  return <section id="thông-tin-trụ-sở" className="relative py-16 bg-background overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />
      <div className="absolute -left-20 top-20 w-96 h-96 bg-primary/5 rounded-lg blur-2xl pointer-events-none" />
      <div className="absolute -right-20 bottom-20 w-96 h-96 bg-ring/5 rounded-lg blur-2xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={cn("max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="inline-flex items-center justify-center px-3 py-1 bg-primary/5 border border-primary/10 rounded-lg mb-6">
            <Building2 className="w-4 h-4 text-primary mr-2" />
            <span className="text-xs font-semibold text-primary tracking-wider uppercase">Liên hệ NUCETECH</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 tracking-tight">
            Thông tin Trụ sở
          </h2>
          
          <p className="text-muted-foreground text-lg leading-relaxed">
            Kết nối với đội ngũ chuyên gia NUCETECH để nhận hỗ trợ kỹ thuật và hợp tác đầu tư. 
            Chúng tôi luôn sẵn sàng lắng nghe và giải đáp mọi thắc mắc của quý đối tác.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-stretch">
          {}
          <div className={cn("w-full lg:w-1/2 min-h-[400px] transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8")}>
            <div className="w-full h-full rounded-lg overflow-hidden border border-border shadow-md grayscale hover:grayscale-0 transition-all duration-500">
              <iframe src="https://www.google.com/maps?q=21.0032167,105.8443898&z=16&output=embed" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="NUCETECH Location" className="w-full h-full min-h-[400px]" />
            </div>
          </div>

          {}
          <div className="w-full lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {contactDetails.map((item, index) => <div key={index} data-index={index} className={cn("group h-full transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")} style={{
            transitionDelay: `${index * 100}ms`
          }}>
                <Card className="h-full border border-border bg-card/50 backdrop-blur-sm hover:border-primary/20 transition-all duration-300">
                  <CardContent className="p-5 flex flex-col h-full items-start">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5 transition-all duration-300">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>
                    
                    <h3 className="text-xl font-bold text-foreground mb-2">
                      {item.title}
                    </h3>
                    
                    <p className="text-muted-foreground mb-6 flex-grow font-medium text-sm">
                      {item.content}
                    </p>

                    {item.href ? <Button variant="ghost" className="w-full justify-between group/btn text-foreground hover:text-primary hover:underline px-0" asChild>
                        
                      </Button> : <div className="w-full pt-4 border-t border-border/30 mt-auto">
                        <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Giờ hành chính</span>
                      </div>}
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}